/* eslint-disable camelcase */
import { datadogLogs } from '@datadog/browser-logs';
import { AxiosResponse } from 'axios';
import {
  addDays,
  addMonths,
  formatISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { getMessageError } from 'utils/helpers';
import { isAxiosError } from 'utils/isAxiosError';

import {
  ActivitiesTopicsResponse,
  AssessmentAnswerV2,
  AssessmentAnswersResponse,
  AssessmentsResult,
  CareProgram,
  CreatePatientRecordsEntries,
  EndUserLogbookResponse,
  EndUserLogbookStates,
  EndUserNotesResponse,
  EndUserNotesStates,
  EndUserSessionsResponse,
  EndUserState,
  GetEndUserProgramsResponse,
  PatientRecordEntryResponse,
  PatientRecords,
  PatientRecordsId,
  PreAssessmentQuestions,
  PreAssessmentResult,
  ProgramListResponse,
  ScoreType,
  TopicsPage,
  WorkflowType,
} from 'services';
import { api } from 'services/api';

import {
  Factor,
  Feeling,
  Mood,
} from 'components/CareMatrixModule/tabs/summary/components/mood-tracker/types';

const getEndUserInfo = async (cognitoId: string) => {
  try {
    return await api.get<void, AxiosResponse<EndUserState>>(
      `/v1/specialist/users/${cognitoId}`,
    );
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving user information'),
    );
  }
};

const getCareMatrixModuleUserData = async (cognitoId: string) => {
  try {
    const { data } = await api.get<EndUserState>(
      `/v1/specialist/users/${cognitoId}`,
    );

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving user information'),
    );
  }
};

const getEndUserSessions = async (timezone: string, cognitoId: string) => {
  try {
    const { data } = await api.get<
      void,
      AxiosResponse<EndUserSessionsResponse>
    >(
      `/v1/specialist/sessions?timezone=${encodeURIComponent(
        timezone,
      )}&status=confirmed,completed,eu_no_show,eu_late_cancel,cp_no_show,cp_late_cancel,no_show,incomplete&user_id=${cognitoId}`,
    );
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(e, 'Unexpected error while retrieving user sessions'),
    );
  }
};

const getEndUserNoteById = async (noteId: string) => {
  try {
    return await api.get<void, AxiosResponse<EndUserNotesResponse>>(
      `/v1/specialist/users/notes/${noteId}`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getEndUserLogbookById = async (noteId: string) => {
  try {
    return await api.get<void, AxiosResponse<EndUserLogbookResponse>>(
      `/v1/specialist/users/logbook/${noteId}`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getEndUserAssessmentsV3 = async (cognitoId: string) => {
  try {
    const { data } = await api.get<void, AxiosResponse<AssessmentsResult[]>>(
      `/v3/specialist/users/${cognitoId}/assessments`,
    );

    return data;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const postEndUserNote = async (body: EndUserNotesStates, id?: string) => {
  try {
    return await api[id !== undefined ? 'put' : 'post']<
      EndUserNotesStates,
      void
    >(`/v1/specialist/users/notes${id !== undefined ? `/${id}` : ''}`, body);
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const deleteEndUserNote = async (id?: string) => {
  try {
    return await api.delete<void, AxiosResponse<void>>(
      `/v1/specialist/users/notes/${id}`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const postEndUserLogbook = async (
  { cognito_user_id: cognitoUserId, ...body }: EndUserLogbookStates,
  id?: string,
) => {
  try {
    return await api[id !== undefined ? 'put' : 'post']<
      EndUserLogbookStates,
      void
    >(
      `/v1/specialist/users/${cognitoUserId}/logbook${
        id !== undefined ? `/${id}` : ''
      }`,
      body,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const deleteEndUserLogbook = async (id?: string, cognitoId?: string) => {
  try {
    return await api.delete<void, AxiosResponse<void>>(
      `/v1/specialist/users/${cognitoId}/logbook/${id}`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getEndUserAssessmentAnswersV2 = async (
  cognitoId: string,
  submissionId: string,
  scoreType?: ScoreType,
) => {
  try {
    const response = await api.get<void, AxiosResponse<AssessmentAnswerV2>>(
      `/v2/specialist/users/${cognitoId}/submissions/${submissionId}`,
      { params: { scoreType } },
    );
    return response.data;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while retrieving end user assessment answers',
      ),
    );
  }
};

const getEndUserPreAssessmentsV2 = async (
  cognitoId: string,
  workflowId: WorkflowType,
) => {
  try {
    const { data } = await api.get<void, AxiosResponse<PreAssessmentQuestions>>(
      `/v1/specialist/users/${cognitoId}/workflows/${workflowId}/answers`,
    );
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while retrieving user pre-assessments',
      ),
    );
  }
};

const getEndUserPreAssessmentsV1 = async (cognitoId: string) => {
  try {
    const { data } = await api.get<void, AxiosResponse<PreAssessmentResult>>(
      `/v1/specialist/users/${cognitoId}/pre-assessments`,
    );
    return data;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error while retrieving user pre-assessments',
      ),
    );
  }
};

const getTopics = async (
  cognitoId: string,
  cursor?: string,
  enrolled = false,
  limit = 3,
) => {
  try {
    let url = `/v1/specialist/users/${cognitoId}/topics?enrolled=${enrolled}&status=delivered&limit=${limit}`;

    if (cursor) {
      url += `&nextCursor=${encodeURIComponent(cursor)}`;
    }
    return await api.get<void, AxiosResponse<ActivitiesTopicsResponse>>(url);
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getTopicsPage = async (cognitoId: string, topicId: number) => {
  try {
    return await api.get<void, AxiosResponse<TopicsPage[]>>(
      `/v1/specialist/users/${cognitoId}/topics/${topicId}/pages`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getProgramsList = async (userId: string) => {
  try {
    return await api.get<void, AxiosResponse<ProgramListResponse[]>>(
      `/v1/specialist/users/${userId}/programs`,
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getUserAssessmentAnswers = async (cognitoId: string, formId: string) => {
  try {
    const response = await api.get<
      void,
      AxiosResponse<AssessmentAnswersResponse>
    >(`/v1/specialist/users/${cognitoId}/forms/${formId}/answers`);
    return response;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const deliverTopics = async (
  userCognitoId: string | undefined,
  topicIds: number[],
) => {
  try {
    const response = await api.post<void, AxiosResponse>(
      `/v1/specialist/users/${userCognitoId}/deliver-topics`,
      { topic_ids: topicIds },
    );

    return response;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const enrollToProgram = async (
  userCognitoId: string | undefined,
  programId: number,
) => {
  try {
    const response = await api.post<void, AxiosResponse>(
      `/v1/specialist/users/${userCognitoId}/enroll-program`,
      { program_id: programId },
    );

    return response;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getEndUserDoc = async (cognitoId: string) => {
  const timezone = encodeURIComponent(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  try {
    return await api.get<void, AxiosResponse<BlobPart>>(
      `/v1/specialist/users/${cognitoId}/reports?timezone=${timezone}`,
      {
        headers: {
          Accept: 'application/pdf',
        },
        responseType: 'blob',
      },
    );
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const confirmedUserNonRisk = async (userCognitoId: string | undefined) => {
  try {
    const response = await api.post<void, AxiosResponse>(
      `/v1/specialist/users/${userCognitoId}/criticality/non-risk`,
    );

    return response;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const confirmedUserWithRisk = async (
  userCognitoId: string | undefined,
  note: string,
) => {
  try {
    const response = await api.post<void, AxiosResponse>(
      `/v1/specialist/users/${userCognitoId}/criticality/risk`,
      { note },
    );

    return response;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const createPatientRecords = async (userCognitoId?: string) => {
  try {
    const { data } = await api.post<void, AxiosResponse<PatientRecordsId>>(
      `/v1/users/${userCognitoId}/patient-records`,
    );

    return data;
  } catch (e) {
    throw new Error(
      getMessageError(
        e,
        'Unexpected error ocurred while posting patient record',
      ),
    );
  }
};

const getPatientRecords = async (userCognitoId?: string) => {
  try {
    const { data } = await api.get<PatientRecords>(
      `/v1/users/${userCognitoId}/patient-records`,
    );

    return data;
  } catch (e) {
    if (isAxiosError(e)) {
      throw e;
    }

    throw new Error(
      getMessageError(
        e,
        'Unexpected error ocurred while getting patient record',
      ),
    );
  }
};

const getPatientRecordsEntries = async (
  userCognitoId: string,
  patientRecordId: string,
  questionId: string,
) => {
  try {
    const { data } = await api.get<
      void,
      AxiosResponse<PatientRecordEntryResponse>
    >(
      `/v1/users/${userCognitoId}/patient-records/${patientRecordId}/questions/${questionId}/entries`,
    );

    return data;
  } catch (e) {
    if (isAxiosError(e)) {
      throw e;
    }

    throw new Error(
      getMessageError(
        e,
        'Unexpected error ocurred while getting patient record entries',
      ),
    );
  }
};

const createPatientRecordsEntry = async ({
  userCognitoId,
  patientRecordId,
  questionId,
  content,
}: CreatePatientRecordsEntries) => {
  try {
    const response = await api.post<void, AxiosResponse<void>>(
      `/v1/users/${userCognitoId}/patient-records/${patientRecordId}/questions/${questionId}/entries`,
      { content },
    );

    return response;
  } catch (e) {
    if (isAxiosError(e)) {
      throw e;
    }

    throw new Error(
      getMessageError(
        e,
        'Unexpected error ocurred while creating patient record entries',
      ),
    );
  }
};

export type Result = {
  date: string;
  score: number;
  description: string;
  submission_id: string;
};

type GetPatientAssessmentsResponse = {
  last_assessment_status: 'todo' | 'doing' | 'done';
  data: {
    GAD: Result[];
    PHQ: Result[];
    WHO: Result[];
  };
};

const getPatientAssessments = async (cognitoId: string) => {
  try {
    const { data } = await api.get<GetPatientAssessmentsResponse>(
      `v4/specialist/users/${cognitoId}/assessments`,
    );

    return data;
  } catch (error) {
    datadogLogs.logger.error(
      '[getPatientAssessments] Error while trying to fetch patient assessments',
      {
        error,
        cognitoId,
      },
    );

    throw new Error(
      '[getPatientAssessments] Error while trying to fetch patient assessments',
    );
  }
};

const getEndUserPrograms = async (cognitoId: string) => {
  try {
    const [activities, programs] = await Promise.all([
      api.get<GetEndUserProgramsResponse>(
        `v1/specialist/users/${cognitoId}/topics?enrolled=false&status=delivered&ack=false&limit=100`,
      ),
      api.get<GetEndUserProgramsResponse>(
        `v1/specialist/users/${cognitoId}/topics?enrolled=true&status=delivered&ack=false&limit=100`,
      ),
    ]);

    return {
      activities: activities.data,
      program: programs.data,
    };
  } catch (error) {
    throw new Error(
      getMessageError(error, 'Unexpected error while retrieving user programs'),
    );
  }
};

const getCareMatrixModuleTopicsPage = async (
  cognitoId: string,
  topicId: number,
) => {
  try {
    const { data } = await api.get<TopicsPage[]>(
      `/v1/specialist/users/${cognitoId}/topics/${topicId}/pages`,
    );

    return data;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

const getCareProgramExecution = async (cognitoId: string) => {
  try {
    const { data } = await api.get<CareProgram[]>(
      `/v1/specialist/users/${cognitoId}/care-program-executions`,
    );

    return data;
  } catch (e) {
    throw new Error(getMessageError(e, 'ERROR.TRY'));
  }
};

export type GetPatientMoodTrackerResponse = {
  _id: string;
  created_at: string;
  sections: {
    assessment_id: string;
    oh_my_form: {
      form_id: string;
      submission_id: string;
      token: string;
    };
    type_form: string;
    fields: {
      field_id: string;
      slug: string;
      answer: {
        title: string;
        value: string;
      };
    }[];
  }[];
  completed: false;
}[];

const getPatientMoodTracker = async (cognitoId: string, currentMonth: Date) => {
  const startDate = formatISO(startOfMonth(currentMonth));
  const endDate = formatISO(startOfMonth(addMonths(currentMonth, 1)));

  try {
    const { data } = await api.get<GetPatientMoodTrackerResponse>(
      `/v1/specialist/users/${cognitoId}/mood-tracking/assessments?start_date=${startDate}&end_date=${endDate}&limit=31`,
    );

    const daysWithMoodTracker = data
      .filter(each => {
        return each.sections.some(
          section =>
            section.type_form === 'mood-tracker-questions' &&
            !!section.fields.length,
        );
      })
      .sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      })
      .map(each => {
        const mood = each.sections
          .find(section => section.type_form === 'mood-tracker-questions')
          ?.fields.find(field => field.slug === 'mood_tracker_mood_question')
          ?.answer.title;

        return {
          date: each.created_at,
          mood: mood as Mood,
        };
      });

    return daysWithMoodTracker;
  } catch (error) {
    throw new Error(
      getMessageError(
        error,
        'Unexpected error while retrieving user mood tracker',
      ),
    );
  }
};

const getPatientMoodTrackerAtDay = async (cognitoId: string, day: Date) => {
  const startDate = formatISO(startOfDay(day));
  const endDate = formatISO(startOfDay(addDays(day, 1)));

  try {
    const { data } = await api.get<GetPatientMoodTrackerResponse>(
      `/v1/specialist/users/${cognitoId}/mood-tracking/assessments?start_date=${startDate}&end_date=${endDate}&limit=10`,
    );

    const mostRecentMoodTracker = data
      .filter(each => {
        return each.sections.some(
          section =>
            section.type_form === 'mood-tracker-questions' &&
            !!section.fields.length,
        );
      })
      .sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      })[0];

    const mood = mostRecentMoodTracker?.sections
      .find(eachSection => eachSection.type_form === 'mood-tracker-questions')
      ?.fields.find(
        eachField => eachField.slug === 'mood_tracker_mood_question',
      )?.answer.title;

    const feelings = mostRecentMoodTracker?.sections
      .find(eachSection => eachSection.type_form === 'mood-tracker-questions')
      ?.fields.find(
        eachField => eachField.slug === 'mood_tracker_emotions_question',
      )
      ?.answer.title.split(',')
      .filter(Boolean);

    const factors = mostRecentMoodTracker?.sections
      .find(eachSection => eachSection.type_form === 'mood-tracker-questions')
      ?.fields.find(
        eachField => eachField.slug === 'mood_tracker_reasons_question',
      )
      ?.answer.title.split(',')
      .filter(Boolean);

    const note = data?.[0].sections
      .find(eachSection => eachSection.type_form === 'mood-tracker-notes')
      ?.fields.find(
        eachField => eachField.slug === 'mood_tracker_journal_question',
      )?.answer.title;

    return {
      mood: mood as Mood,
      feelings: feelings as Feeling[],
      factors: factors as Factor[],
      note,
    };
  } catch (error) {
    throw new Error(
      getMessageError(
        error,
        `Unexpected error while retrieving user mood tracker at day ${day}`,
      ),
    );
  }
};

type CareMatrixModuleHistoryResponse = {
  care_matrix_profile: string;
  program_id: number;
  title_key: string;
  deliver_date: string;
  topics: {
    id: number;
    title_key: string;
  }[];
}[];

const getEndUserCareMatrixHistory = async (cognitoId: string) => {
  try {
    const { data } = await api.get<CareMatrixModuleHistoryResponse>(
      `v1/specialist/users/${cognitoId}/care-program-executions/history`,
    );

    return data;
  } catch (error) {
    datadogLogs.logger.error(
      '[getEndUserCareMatrixHistory] Error while trying to fetch user care matrix history',
      {
        error,
        cognitoId,
      },
    );

    throw new Error(
      '[getEndUserCareMatrixHistory] Error while trying to fetch user care matrix history',
    );
  }
};

type GetEndUserActivitiesHistoryResponse = {
  metadata: {
    pages: number;
    nextCursor: string;
  };
  results: {
    id: number;
    titleKey: string;
    program: string;
    programId: number;
    deliverDate: string;
    progress: {
      total: number;
      acknowledged: number;
    };
  }[];
};

const getEndUserActivitiesHistory = async (cognitoId: string) => {
  try {
    const { data } = await api.get<GetEndUserActivitiesHistoryResponse>(
      `v1/specialist/users/${cognitoId}/topics?enrolled=false&limit=99999999&ack=true`,
    );

    return data;
  } catch (error) {
    datadogLogs.logger.error(
      '[getEndUserActivitiesHistory] Error while trying to fetch user activity history',
      {
        error,
        cognitoId,
      },
    );

    throw new Error(
      '[getEndUserActivitiesHistory] Error while trying to fetch user activity history',
    );
  }
};

type GetEndUserProgramsHistoryResponse = {
  program_id: number;
  title_key: string;
  deliver_date: string;
  topics: {
    id: number;
    title_key: string;
  }[];
}[];

const getEndUserProgramsHistory = async (cognitoId: string) => {
  try {
    const { data } = await api.get<GetEndUserProgramsHistoryResponse>(
      `v1/specialist/users/${cognitoId}/programs/history?limit=100`,
    );

    return data;
  } catch (error) {
    datadogLogs.logger.error(
      '[getEndUserProgramsHistory] Error while trying to fetch user activity history',
      {
        error,
        cognitoId,
      },
    );

    throw new Error(
      '[getEndUserProgramsHistory] Error while trying to fetch user activity history',
    );
  }
};

export {
  getEndUserInfo,
  getEndUserSessions,
  getEndUserNoteById,
  getEndUserLogbookById,
  postEndUserNote,
  postEndUserLogbook,
  deleteEndUserNote,
  deleteEndUserLogbook,
  getEndUserPreAssessmentsV2,
  getProgramsList,
  getTopics,
  getTopicsPage,
  getUserAssessmentAnswers,
  deliverTopics,
  enrollToProgram,
  getEndUserDoc,
  confirmedUserNonRisk,
  confirmedUserWithRisk,
  createPatientRecords,
  getPatientRecords,
  getPatientRecordsEntries,
  createPatientRecordsEntry,
  getEndUserAssessmentsV3,
  getEndUserPreAssessmentsV1,
  getEndUserAssessmentAnswersV2,
  getCareMatrixModuleUserData,
  getPatientAssessments,
  getEndUserPrograms,
  getCareMatrixModuleTopicsPage,
  getCareProgramExecution,
  getPatientMoodTracker,
  getPatientMoodTrackerAtDay,
  getEndUserCareMatrixHistory,
  getEndUserActivitiesHistory,
  getEndUserProgramsHistory,
};
